import React, { useEffect, useRef } from 'react';
import { Line } from '@antv/g2plot';
import type { ChartField } from '@/types/chat';
import { CHART_COLORS } from '@/common/constants';
import { toNum } from '@/utils';

interface LineChartProps {
  data: Record<string, unknown>[];
  dimensions?: ChartField[];
  metrics?: ChartField[];
}

/**
 * 宽表转长表（fold）—— 单指标和多指标统一走此路径
 *
 * 输入：[{ bizdate: '20260315', order_cnt: 390427 }, ...]
 * 输出：[
 *   { bizdate: '20260315', __value: 390427, __type: '订单数' },
 *   ...
 * ]
 */
function foldData(
  data: Record<string, unknown>[],
  xField: string,
  metricFields: ChartField[],
): Record<string, unknown>[] {
  const result: Record<string, unknown>[] = [];
  for (const row of data) {
    for (const m of metricFields) {
      const raw = row[m.code];
      result.push({
        [xField]: row[xField],
        __value: toNum(raw),
        __type: m.name ?? m.code,
      });
    }
  }
  return result;
}

export const LineChart: React.FC<LineChartProps> = ({ data, dimensions, metrics }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<Line | null>(null);

  const resolveFields = (d: Record<string, unknown>[]) => {
    const keys = d?.length ? Object.keys(d[0]) : [];
    const xField = dimensions?.[0]?.code ?? keys[0] ?? 'x';
    const metricFields: ChartField[] = metrics?.length
      ? metrics
      : keys.slice(1).map((k) => ({ code: k, name: k }));
    return { xField, metricFields };
  };

  /** 统一 fold，单指标时只有 1 条折线，多指标时有多条 */
  const buildChartData = (d: Record<string, unknown>[]) => {
    const { xField, metricFields } = resolveFields(d);
    return {
      xField,
      metricFields,
      chartData: foldData(d, xField, metricFields),
      yField: '__value',
      seriesField: '__type',
      isMultiMetric: metricFields.length > 1,
    };
  };

  useEffect(() => {
    if (!containerRef.current || !data?.length) return;

    const { xField, metricFields, chartData, yField, seriesField, isMultiMetric } =
      buildChartData(data);

    const xTitle = dimensions?.[0]?.name ?? dimensions?.[0]?.code ?? xField;
    const yTitle = metricFields.map((m) => m.name ?? m.code).join(' & ');

    chartRef.current = new Line(containerRef.current, {
      data: chartData,
      xField,
      yField,
      seriesField,
      autoFit: true,
      height: 260,
      smooth: true,
      color: CHART_COLORS,
      xAxis: {
        title: { text: xTitle },
        label: { autoRotate: false },
      },
      yAxis: {
        title: { text: yTitle },
        nice: true,
        tickCount: 5,
      },
      /** 单指标隐藏图例；多指标显示图例区分各折线 */
      legend: isMultiMetric ? { position: 'top' } : false,
      point: { size: 3, shape: 'circle' },
      tooltip: {
        showMarkers: true,
        /** 用 __type 作为系列名（已是 metric.name），__value 作为值 */
        fields: [xField, '__value', '__type'],
        formatter: (datum: Record<string, unknown>) => ({
          name: String(datum['__type'] ?? ''),
          value: datum['__value'] as number,
        }),
      },
    });

    chartRef.current.render();

    return () => {
      chartRef.current?.destroy();
      chartRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!chartRef.current || !data?.length) return;
    const { chartData } = buildChartData(data);
    chartRef.current.changeData(chartData);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return <div ref={containerRef} style={{ height: '100%', width: '100%' }} />;
};
